import React, { useState, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Row, Col } from 'antd';
import { mapValues, groupBy, omit, isEmpty } from 'lodash';

import FilterBar from './FilterBar';
import BasketItemsTable from './BasketItemsTable';
import DetailsBar from './DetailsBar';
import BasketItemsBarChart from './BasketItemsBarChart';
import Charts from './charts/Charts';
import { getRingColor } from '../../../../../../shared/designSystem/components/BreakdownRingChart';

const aggregateData = (data) =>
  data && data.length
    ? mapValues(groupBy(data, 'ORDER_ID'), (list) => list.map((obj) => omit(obj, 'ORDER_ID')))
    : {};

const sortData = (data) =>
  data && data.length
    ? data.sort((a, b) => moment(a.TIMESTAMP || a.DATE).valueOf() - moment(b.TIMESTAMP || b.DATE).valueOf())
    : [];

const BasketItemsLayout = withRouter(
  ({
    chartData,
    location,
    permission,
    reportName,
    searchTerm,
    setSearchTerm,
    setAttributionWindow = () => {}, // Default to no-op function
    campaignOrderId,
    chartsLoading,
  }) => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const orderId = params.get('orderId') || '';
    const dropdownFilter = params.get('df');
    const [currentPage, setCurrentPage] = useState(1);
    const [orderIdVal, setOrderIdVal] = useState(orderId);

    const sortedData = useMemo(
      () => (chartData && chartData.data ? sortData(chartData.data) : []),
      [chartData]
    );

    const filterByOrderId = useMemo(() => {
      const lowerCaseOrderId = orderId.toLowerCase();
      return (obj) => obj.ORDER_ID.toLowerCase().includes(lowerCaseOrderId);
    }, [orderId]);

    const filterBySearchTerm = useMemo(() => {
      const searchStrings = search.split(',').map((str) => str.trim().toLowerCase());
      return (obj) => searchStrings.some((searchStr) => obj.NAME.toLowerCase().includes(searchStr));
    }, [search]);

    const daysMapping = {
      'Last 7 Days': 7,
      'Last 15 Days': 15,
      'Last 30 Days': 30,
      'Last 60 Days': 60,
      'Last 90 Days': 90,
    };

    const filterDataByDate = (data, days) => {
      const endDate = moment();
      const startDate = moment().subtract(days, 'days');
      return data.filter((obj) => {
        const date = moment(obj.TIMESTAMP || obj.DATE);
        return date.isBetween(startDate, endDate, null, '[]');
      });
    };

    const filteredData = useMemo(() => {
      const getFilteredData = (data) => {
        let filteredData = data;
        if (orderId) filteredData = filteredData.filter(filterByOrderId);
        if (search) {
          filteredData = filteredData.filter(filterBySearchTerm);
          if (setSearchTerm) setSearchTerm(search);
        }
        if (dropdownFilter && dropdownFilter !== 'All Time') {
          const days = daysMapping[dropdownFilter];
          if (days) {
            setAttributionWindow(days);
            filteredData = filterDataByDate(filteredData, days);
          }
        }
        return filteredData;
      };
      return getFilteredData(sortedData);
    }, [
      orderId,
      search,
      dropdownFilter,
      sortedData,
      filterByOrderId,
      filterBySearchTerm,
      setSearchTerm,
      setAttributionWindow,
      daysMapping,
    ]);

    const aggregatedData = useMemo(() => aggregateData(filteredData), [filteredData]);

    const [tableData, setTableData] = useState([]);
    const [exportData, setExportData] = useState([]);

    const sortedByName = useMemo(
      () =>
        filteredData.length
          ? filteredData.sort((a, b) => {
              const nameA = a && a.NAME ? a.NAME : ''; // Fallback to empty string if null/undefined
              const nameB = b && b.NAME ? b.NAME : ''; // Fallback to empty string if null/undefined
              return nameA.localeCompare(nameB);
            })
          : [],
      [filteredData]
    );

    const filteredByName = useMemo(
      () =>
        sortedByName.filter(
          (item) =>
            item.ORDER_ID.toLowerCase().includes(search.toLowerCase().trim()) ||
            item.NAME.toLowerCase().includes(search.toLowerCase().trim())
        ),
      [sortedByName, search]
    );

    useEffect(() => {
      const revenueTableAggregatedData = isEmpty(filteredByName)
        ? {}
        : groupBy(filteredByName, 'NAME');

      const updatedTableData = [];
      let updatedTotalRevenue = 0;
      let updatedTotalQuantity = 0;

      Object.keys(revenueTableAggregatedData).forEach((key) => {
        const byNameDataArray = revenueTableAggregatedData[key];
        let revenue = 0;
        let quantity = 0;

        byNameDataArray.forEach((item) => {
          revenue += item.PRICE * item.QUANTITY;
          quantity += item.QUANTITY;
        });

        updatedTableData.push({
          NAME: key,
          REVENUE: revenue,
          QUANTITY: quantity,
        });

        updatedTotalRevenue += revenue;
        updatedTotalQuantity += quantity;
      });

      // Sort revenue-desc so top sellers surface first — matches V2's
      // BasketItemDetails feed-order and lets the table row order line up
      // with the breakdown donut's slice order. Alphabetical is one
      // header-click away via DataTable's sortable Product Name column.
      updatedTableData.sort(
        (a, b) => (Number(b.REVENUE) || 0) - (Number(a.REVENUE) || 0)
      );

      const updatedExportData = [
        ...updatedTableData,
        {
          NAME: 'TOTAL',
          REVENUE: updatedTotalRevenue,
          QUANTITY: updatedTotalQuantity,
        },
      ];

      setTableData((prev) =>
        JSON.stringify(prev) !== JSON.stringify(updatedTableData) ? updatedTableData : prev
      );
      setExportData((prev) =>
        JSON.stringify(prev) !== JSON.stringify(updatedExportData) ? updatedExportData : prev
      );
    }, [filteredByName]);

    const colorValuePairs = useMemo(
      () => tableData.map((el, index) => ({ ...el, color: getRingColor(index) })),
      [tableData]
    );

    return (
      <div className="mjl-ds campaign-basket-items-v1">
        <FilterBar
          data={exportData}
          setOrderIdVal={setOrderIdVal}
          params={params}
          dropdownFilter={dropdownFilter}
          permission={permission}
          setSearchTerm={setSearchTerm}
          setAttributionWindow={setAttributionWindow}
          setCurrentPage={setCurrentPage}
        />

        {/*
          Two-pane layout preserved from V1: KPIs + chart + revenue breakdown
          take the left 75%, the narrow orders table sits on the right 25%.
          Unified Performance card wraps KPI bar + trend chart, matching the
          V2 BasketItemsPerformance / Summary / Display / Transactions V2
          pattern. Date filtering stays on the top FilterBar's All-Time
          dropdown — no duplicate control in the Performance head.
        */}
        <Row gutter={[16, 16]} className="campaign-basket-items-v1__body">
          <Col xs={24} lg={18}>
            <section className="campaign-overview__performance">
              <header className="campaign-overview__performance-head">
                <div className="campaign-overview__performance-eyebrow mono-eyebrow">
                  Performance
                </div>
              </header>

              <DetailsBar
                data={filteredData}
                aggregatedData={aggregatedData}
                chartsLoading={chartsLoading}
              />

              <BasketItemsBarChart data={filteredData} />
            </section>

            <Charts chartData={colorValuePairs} chartsLoading={false} />
          </Col>
          <Col xs={24} lg={6}>
            <BasketItemsTable
              orderIdVal={orderIdVal}
              setOrderIdVal={setOrderIdVal}
              aggregatedData={aggregatedData}
              reportName={reportName}
              campaignOrderId={campaignOrderId}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              searchTerm={searchTerm}
            />
          </Col>
        </Row>
      </div>
    );
  }
);

export default BasketItemsLayout;
